import { Drawer, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { getDir } from "orion-components/i18n/Config/selector";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@mui/material";
import { withStyles } from "@mui/styles";
import PropTypes from "prop-types";
import ShapeSelector from "./components/ShapeSelector";
import { setMapTools } from "orion-components/Map/Tools/Actions";
import { clearShapes } from "orion-components/GlobalData/Shapes/actions";
import { validateShape } from "orion-components/Map/helpers";
import { getTranslation, Translate } from "orion-components/i18n";
import EventBus from "orion-components/SharedComponents/EventBus";

const styles = {
	root: {
		"&.Mui-focused": {
			backgroundColor: "rgb(31, 31, 33)"
		}
	},
	paper: {
		top: 48,
		width: 431,
		height: "calc(100% - 48px)",
		overflowY: "unset"
	},
	input: {
		"&::placeholder": {
			fontSize: 14,
			color: "#828283"
		},
		height: "unset!important",
		color: "#fff"
	},
	selected: {
		backgroundColor: "rgba(255, 255, 255, 0.16)!important"
	},
	containedRoot: {
		"&:hover": {
			backgroundColor: "rgba(0, 188, 212, 0.8)"
		},
		fontSize: "12px",
		width: 88,
		backgroundColor: "rgb(0, 188, 212)",
		color: "#fff"
	},
	containedFocusVisible: {
		backgroundColor: "rgba(0, 188, 212, 0.4)"
	},
	containedLabel: {
		color: "white"
	},
	disabled: {
		opacity: "0.3",
		color: "#fff!important",
		backgroundColor: "rgba(255, 255, 255, 0.12)!important"
	}
};

const ShapeEditor = ({ classes, open, handleSave }) => {
	const dir = useSelector((state) => getDir(state));
	const dispatch = useDispatch();
	const { mapTools } = useSelector((state) => state.mapState);
	const app = useSelector((state) => state.appId);
	// eslint-disable-next-line no-unused-vars
	const [displayOnEventActive, setDisplayOnEventActive] = useState(app === "events-app" ? true : false);
	const [disableSave, setDisableSave] = useState(true);
	const shapesReducer = useSelector((state) => state.shapes);
	const [error, setError] = useState(null);

	useEffect(() => {
		const { feature } = mapTools;

		if (feature) {
			const { properties } = feature;
			const { displayOnEventActive = false } = properties;
			setDisplayOnEventActive(displayOnEventActive);
		}
	}, []);

	const handleClose = () => {
		dispatch(clearShapes());
		dispatch(setMapTools({ type: null }));
		EventBus.publish("drawClearAll");
	};

	const handleKeydown = (e) => {
		if (e.key === "Escape") {
			handleClose();
		}
	};

	useEffect(() => {
		document.addEventListener("keydown", handleKeydown);
		return () => {
			document.removeEventListener("keydown", handleKeydown);
		};
	}, [handleKeydown]);

	const onSubmit = () => {
		const { feature } = mapTools;
		const { geometry } = feature;
		const { type } = geometry;
		let shapeProperties = {};
		setError(null);

		if (type === "Point") {
			const { name, description, symbol, symbolCategory, displayOnEventActive } = shapesReducer["point"];
			shapeProperties = {
				name,
				description,
				displayOnEventActive,
				symbol,
				type,
				symbolCategory
			};
		} else if (type === "LineString") {
			const { name, description, polyStroke, lineWidth, lineType, lineStrokeOpacity, displayOnEventActive } =
				shapesReducer["lineString"];
			shapeProperties = {
				name,
				description: description || "",
				displayOnEventActive,
				symbol: null,
				type,
				polyStroke: polyStroke || "2face8",
				lineWidth: lineWidth || 3,
				lineType: lineType || "Solid",
				lineStrokeOpacity: lineStrokeOpacity / 100 || 1
			};
		} else if (
			type === "Polygon" &&
			shapesReducer.selectedShape !== "Rectangle" &&
			shapesReducer.selectedShape !== "Circle"
		) {
			const {
				name,
				description,
				polyStroke,
				lineWidth,
				lineType,
				polyFill,
				polyFillOpacity,
				lineStrokeOpacity,
				displayOnEventActive
			} = shapesReducer["polygon"];
			shapeProperties = {
				name,
				description,
				displayOnEventActive,
				symbol: null,
				type,
				polyFill: polyFill || "0073c8",
				polyFillOpacity: polyFillOpacity / 100 || 0.2,
				polyStroke: polyStroke || "2face8",
				lineWidth: lineWidth || 3,
				lineType: lineType || "Solid",
				lineStrokeOpacity: lineStrokeOpacity / 100 || 1
			};
		} else if (shapesReducer.selectedShape === "Rectangle") {
			const {
				name,
				description,
				constrainProportion,
				polyFill,
				polyFillOpacity,
				polyStroke,
				lineWidth,
				lineType,
				lineStrokeOpacity,
				displayOnEventActive
			} = shapesReducer["rectangle"];
			const type = "Polygon";
			shapeProperties = {
				name,
				isRectangle: true,
				description,
				displayOnEventActive,
				symbol: null,
				type,
				constrainProportion,
				polyFill: polyFill || "0073c8",
				polyFillOpacity: polyFillOpacity / 100 || 0.2,
				polyStroke: polyStroke || "2face8",
				lineWidth: lineWidth || 3,
				lineType: lineType || "Solid",
				lineStrokeOpacity: lineStrokeOpacity / 100 || 1
			};
		} else if (shapesReducer.selectedShape === "Circle") {
			const {
				name,
				description,
				polyFill,
				polyFillOpacity,
				polyStroke,
				lineWidth,
				lineType,
				lineStrokeOpacity,
				otherFeatures,
				displayOnEventActive
			} = shapesReducer["circle"];
			const type = "Polygon";
			shapeProperties = {
				name,
				isCircle: true,
				description,
				displayOnEventActive,
				symbol: null,
				type,
				polyFill: polyFill || "0073c8",
				polyFillOpacity: polyFillOpacity / 100 || 0.2,
				polyStroke: polyStroke || "2face8",
				lineWidth: lineWidth || 3,
				lineType: lineType || "Solid",
				lineStrokeOpacity: lineStrokeOpacity / 100 || 1,
				otherFeatures
			};
		} else {
			handleClose();
			return;
		}

		if (!validateShape(geometry)) {
			setError(getTranslation("global.sharedComponents.shapeEdit.errorText.shapesErr"));
			return;
		}

		handleSave(shapeProperties);
		handleClose();
	};

	useEffect(() => {
		const { feature } = mapTools;
		if (feature) {
			const { geometry } = feature;
			const { type } = geometry;
			if (type === "Point") {
				const { name, symbol } = shapesReducer["point"];
				if (!name || name === "" || symbol === "" || geometry.coordinates.length === 0) {
					setDisableSave(true);
				} else {
					setDisableSave(false);
				}
			} else if (type === "LineString") {
				const { name } = shapesReducer["lineString"];
				if (!name || name === "" || geometry.coordinates.length === 0) {
					setDisableSave(true);
				} else {
					setDisableSave(false);
				}
			} else if (
				type === "Polygon" &&
				shapesReducer.selectedShape !== "Rectangle" &&
				shapesReducer.selectedShape !== "Circle"
			) {
				const { name } = shapesReducer["polygon"];
				if (!name || name === "" || geometry.coordinates.length === 0) {
					setDisableSave(true);
				} else {
					setDisableSave(false);
				}
			} else if ((type === "Polygon" || type === "Rectangle") && shapesReducer.selectedShape === "Rectangle") {
				const { name } = shapesReducer["rectangle"];
				if (!name || name === "" || geometry.coordinates.length === 0) {
					setDisableSave(true);
				} else {
					setDisableSave(false);
				}
			} else if ((type === "Polygon" || type === "Circle") && shapesReducer.selectedShape === "Circle") {
				const { name } = shapesReducer["circle"];
				if (!name || name === "" || geometry.coordinates.length === 0) {
					setDisableSave(true);
				} else {
					setDisableSave(false);
				}
			}
		} else {
			setDisableSave(true);
		}
	}, [shapesReducer, mapTools]);

	const overrides = {
		cancel: {
			textTransform: "none",
			color: "#A7ACB0",
			fontFamily: "Roboto",
			fontWeight: "medium",
			fontSize: 12,
			"&:hover": {
				backgroundColor: "rgba(255, 255, 255, 0.08)"
			}
		}
	};

	return (
		<Drawer
			open={open}
			anchor={dir === "rtl" ? "right" : "left"}
			variant="persistent"
			classes={{ paper: classes.paper }}
		>
			<div className={`shape-editor${dir == "rtl" ? "RTL" : ""}`} style={{ width: 431 }}>
				<div
					style={{
						height: 74,
						display: "flex",
						justifyContent: dir === "ltr" ? "flex-end" : "flex-start",
						backgroundColor: "#232429"
					}}
				>
					<Button
						id="cancel-shape"
						color="primary"
						variant="text"
						style={{ marginTop: 16, marginBottom: 16, marginRight: 48 }}
						sx={overrides.cancel}
						disableTouchRipple={true}
						onClick={handleClose}
					>
						<Translate value="global.sharedComponents.shapeEditor.cancel" />
					</Button>
					<Button
						id="save-shape"
						color="primary"
						variant="contained"
						style={{ marginTop: 16, marginBottom: 16, marginRight: 20 }}
						sx={{
							textTransform: "none",
							color: "#FFFFFF",
							padding: "14px 42px",
							fontFamily: "Roboto",
							fontWeight: "medium",
							fontSize: 12,
							background: "#4DB5F4",
							backgroundPosition: "0% 0%",
							backgroundRepeat: "no-repeat",
							backgroundSize: "padding-box",
							boxShadow: "0px 2px 3px #00000029",
							borderRadius: "5px",
							opacity: 1
						}}
						disableTouchRipple={true}
						onClick={onSubmit}
						disabled={disableSave}
						classes={{
							root: classes.containedRoot,
							label: classes.containedLabel,
							focusVisible: classes.containedFocusVisible,
							disabled: classes.disabled
						}}
					>
						<Translate value="global.sharedComponents.shapeEditor.save" />
					</Button>
				</div>
				<div style={{ height: "calc(100vh - 120px)", overflowY: "scroll" }}>
					<ShapeSelector />
				</div>
				{/* Error Messaging */}
				{error && (
					<Typography style={{ padding: "0px 12px" }} color="error" variant="caption">
						{error}
					</Typography>
				)}
			</div>
		</Drawer >
	);
};
ShapeEditor.propTypes = {
	classes: PropTypes.shape({
		paper: PropTypes.string
	}).isRequired,
	open: PropTypes.bool.isRequired,
	handleSave: PropTypes.func.isRequired
};

export default withStyles(styles)(ShapeEditor);
